import { memo, useState, useEffect } from "react";
import { Handle, Position } from "@xyflow/react";
import { 
  Sparkles, 
  Download, 
  Maximize2, 
  MoreHorizontal,
  Trash2,
  ChevronDown,
  Loader2,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";

const MODELS = [
  { id: "nano-banana-2", name: "Nano Banana 2", provider: "Nano" },
];

const VisualGenerateNode = memo(({ data, selected, id }) => {
  const [model, setModel] = useState(data.config?.model || "nano-banana-2");
  const [imageUrl, setImageUrl] = useState(data.config?.output_image || data.config?.image || null);
  const [isGenerating, setIsGenerating] = useState(false);

  // Update image when config changes (from workflow execution results)
  useEffect(() => {
    if (data.config?.image) {
      setImageUrl(data.config.image);
    }
  }, [data.config?.image]);

  const selectedModel = MODELS.find(m => m.id === model) || MODELS[0];

  const handleModelChange = (newModel) => {
    setModel(newModel);
    data.onUpdate?.({ model: newModel });
  };

  return (
    <div
      className={`group relative rounded-xl transition-all ${
        selected 
          ? "ring-2 ring-indigo-500 ring-offset-2 ring-offset-[#000000]" 
          : "ring-1 ring-slate-700/50 hover:ring-slate-600"
      }`}
      style={{ minWidth: 240, maxWidth: 320 }}
    >
      {/* Input Handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="input"
        className="!w-5 !h-5 !bg-purple-500 !border-[3px] !border-slate-900 hover:!bg-purple-400 hover:!scale-125 !transition-all !cursor-crosshair"
      />

      {/* Header */}
      <div className="bg-slate-900/90 backdrop-blur px-3 py-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-purple-400" />
          <span className="text-xs font-medium text-slate-200 truncate max-w-[100px]">
            {data.label || "Generate Image"}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[10px] text-slate-500 bg-slate-800 px-1.5 py-0.5 rounded">
            {selectedModel.provider}
          </span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-1 hover:bg-slate-800 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                <MoreHorizontal className="w-3.5 h-3.5 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-slate-900 border-slate-700">
              <DropdownMenuItem 
                onClick={() => data.onDelete?.()} 
                className="text-red-400 focus:text-red-400 focus:bg-red-500/10"
              >
                <Trash2 className="w-3.5 h-3.5 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Image/Result Area */}
      <div className="bg-slate-800/50 aspect-[4/3] relative">
        {isGenerating ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <Loader2 className="w-8 h-8 text-purple-400 animate-spin mb-2" />
            <span className="text-xs text-slate-400">Generating...</span>
          </div>
        ) : imageUrl ? (
          <>
            <img 
              src={imageUrl} 
              alt={data.label} 
              className="w-full h-full object-cover"
            />
            <div className="absolute bottom-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button className="p-1.5 bg-slate-900/80 backdrop-blur rounded hover:bg-slate-800">
                <Download className="w-3.5 h-3.5 text-slate-300" />
              </button>
              <button className="p-1.5 bg-slate-900/80 backdrop-blur rounded hover:bg-slate-800">
                <Maximize2 className="w-3.5 h-3.5 text-slate-300" />
              </button>
            </div>
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-500">
            <Sparkles className="w-8 h-8 mb-2 opacity-50" />
            <span className="text-xs">Output will appear here</span>
          </div>
        )}
      </div>

      {/* Model Selector */}
      <div className="bg-slate-900/90 backdrop-blur px-3 py-2 border-t border-slate-700/50">
        <Select value={model} onValueChange={handleModelChange}>
          <SelectTrigger className="h-7 text-xs bg-slate-800/50 border-slate-700">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700">
            {MODELS.map((m) => (
              <SelectItem key={m.id} value={m.id} className="text-xs">
                {m.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className="!w-5 !h-5 !bg-purple-500 !border-[3px] !border-slate-900 hover:!bg-purple-400 hover:!scale-125 !transition-all !cursor-crosshair"
      />
    </div>
  );
});

VisualGenerateNode.displayName = "VisualGenerateNode";
export default VisualGenerateNode;
